import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';

interface PortalUser {
  id: string;
  email: string;
  displayName: string;
  typ: 'kunde' | 'sub';
  status: 'pending_invite' | 'active' | 'disabled';
  kundeId: string | null;
  subunternehmerId: string | null;
  inviteExpiresAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

interface PortalZuordnung {
  id: string;
  portalUserId: string;
  ressourceTyp: 'objekt' | 'projekt' | 'auftrag';
  objektId: string | null;
  projektId: string | null;
  auftragId: string | null;
  rolle: 'hausverwaltung' | 'mietverwaltungs_eigentuemer' | 'verwalter' | null;
  einheitText: string | null;
  von: string;
  bis: string | null;
  createdAt: string;
}

interface ObjektRef {
  id: string;
  bezeichnung: string;
  adresseStrasse: string | null;
}

interface AuftragRef {
  id: string;
  titel: string;
  status: string;
}

export const Route = createFileRoute('/portal-users/$id')({
  beforeLoad: () => requireAccess('/api/v1/portal-users'),
  component: PortalUserDetailPage,
});

function PortalUserDetailPage() {
  const { id } = Route.useParams();
  const list = useQuery({
    queryKey: ['portal-users'],
    queryFn: () => api<{ items: PortalUser[] }>('/api/v1/portal-users'),
  });
  const user = list.data?.items.find((u) => u.id === id);

  return (
    <div className="space-y-4">
      <PageHeader
        title={user?.email ?? 'Portal-Benutzer'}
        description={
          user
            ? `${user.displayName} · ${user.typ === 'kunde' ? 'Kunde' : 'Subunternehmer'}-Zugang`
            : 'Lade …'
        }
        actions={
          <Link to="/portal-users" className="text-sm text-brand-700 hover:underline">
            ← Zurueck zur Liste
          </Link>
        }
      />

      {!user && <div className="text-slate-500">Lade…</div>}
      <ErrorBanner error={list.error} />

      {user && (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Stammdaten</h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-slate-500">Status</dt>
              <dd>{user.status}</dd>
              <dt className="text-slate-500">Letzter Login</dt>
              <dd>{user.lastLoginAt ?? '–'}</dd>
              <dt className="text-slate-500">Eingeladen am</dt>
              <dd>{user.createdAt.slice(0, 10)}</dd>
              {user.inviteExpiresAt && (
                <>
                  <dt className="text-slate-500">Einladungs-Token gueltig bis</dt>
                  <dd>{user.inviteExpiresAt.slice(0, 16).replace('T', ' ')}</dd>
                </>
              )}
            </dl>
          </div>

          <ZuordnungenSection user={user} />
        </>
      )}
    </div>
  );
}

function ZuordnungenSection({ user }: { user: PortalUser }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['portal-zuordnungen', user.id],
    queryFn: () => api<{ items: PortalZuordnung[] }>(`/api/v1/portal-users/${user.id}/zuordnungen`),
  });
  const objekte = useQuery({
    queryKey: ['objekte-for-zuord'],
    queryFn: () => api<{ items: ObjektRef[] }>('/api/v1/objekte'),
  });
  const auftraege = useQuery({
    queryKey: ['auftraege-for-zuord'],
    queryFn: () => api<{ items: AuftragRef[] }>('/api/v1/auftraege'),
    enabled: user.typ === 'sub',
  });
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          Zuordnungen ({list.data?.items.length ?? 0})
        </h3>
        <Button variant={showForm ? 'ghost' : 'primary'} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Abbrechen' : '+ Zuordnung hinzufuegen'}
        </Button>
      </div>

      {showForm && (
        <ZuordnungCreateForm
          user={user}
          objekte={objekte.data?.items ?? []}
          auftraege={auftraege.data?.items ?? []}
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ['portal-zuordnungen', user.id] });
          }}
        />
      )}

      {list.data && list.data.items.length === 0 && (
        <p className="text-sm text-slate-400 py-4 text-center">
          Noch keine Zuordnung.{' '}
          {user.typ === 'kunde'
            ? 'Hausverwaltung / Eigentuemer brauchen mindestens ein Objekt.'
            : 'Subunternehmer brauchen mindestens einen Auftrag oder ein Projekt.'}
        </p>
      )}

      {list.data && list.data.items.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1 text-left">Typ</th>
              <th className="px-2 py-1 text-left">Ziel</th>
              <th className="px-2 py-1 text-left">Rolle</th>
              <th className="px-2 py-1 text-left">Von</th>
              <th className="px-2 py-1 text-left">Bis</th>
              <th className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.data.items.map((z) => (
              <ZuordnungRow
                key={z.id}
                z={z}
                objekte={objekte.data?.items ?? []}
                auftraege={auftraege.data?.items ?? []}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ZuordnungCreateForm({
  user,
  objekte,
  auftraege,
  onDone,
}: {
  user: PortalUser;
  objekte: ObjektRef[];
  auftraege: AuftragRef[];
  onDone: () => void;
}) {
  // Sub darf nur projekt|auftrag, Kunde alle drei (objekt-default).
  const allowedTypen: PortalZuordnung['ressourceTyp'][] =
    user.typ === 'sub' ? ['auftrag', 'projekt'] : ['objekt', 'auftrag', 'projekt'];
  const [ressourceTyp, setRessourceTyp] = useState<PortalZuordnung['ressourceTyp']>(
    allowedTypen[0]!,
  );
  const [objektId, setObjektId] = useState('');
  const [auftragId, setAuftragId] = useState('');
  const [rolle, setRolle] = useState<NonNullable<PortalZuordnung['rolle']>>('hausverwaltung');
  const [von, setVon] = useState(new Date().toISOString().slice(0, 10));
  const [bis, setBis] = useState('');
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        portalUserId: user.id,
        ressourceTyp,
        von,
      };
      if (ressourceTyp === 'objekt') {
        body['objektId'] = objektId;
        body['rolle'] = rolle;
      } else if (ressourceTyp === 'auftrag') {
        body['auftragId'] = auftragId;
      } else if (ressourceTyp === 'projekt') {
        // Projekt-Picker fehlt noch — Stufe-D-Projekt-CRUD existiert nicht.
        throw new Error('Projekt-Zuordnungen sind noch nicht im UI verfuegbar (Stufe D).');
      }
      if (bis) body['bis'] = bis;
      return api<{ item: PortalZuordnung }>('/api/v1/portal-zuordnungen', {
        method: 'POST',
        body,
      });
    },
    onSuccess: () => onDone(),
    onError: (err) => setError(err),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-brand-200 bg-brand-50/40 p-3 space-y-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Ressource-Typ</label>
          <select
            value={ressourceTyp}
            onChange={(e) => setRessourceTyp(e.target.value as PortalZuordnung['ressourceTyp'])}
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            {allowedTypen.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {ressourceTyp === 'objekt' && (
          <>
            <div>
              <label className="text-xs font-medium text-slate-600">Objekt</label>
              <select
                value={objektId}
                onChange={(e) => setObjektId(e.target.value)}
                required
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">– waehlen –</option>
                {objekte.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.bezeichnung}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Rolle</label>
              <select
                value={rolle}
                onChange={(e) => setRolle(e.target.value as NonNullable<PortalZuordnung['rolle']>)}
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="hausverwaltung">Hausverwaltung</option>
                <option value="mietverwaltungs_eigentuemer">Mietverwaltungs-Eigentuemer</option>
                <option value="verwalter">Verwalter</option>
              </select>
            </div>
          </>
        )}

        {ressourceTyp === 'auftrag' && (
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-600">Auftrag</label>
            <select
              value={auftragId}
              onChange={(e) => setAuftragId(e.target.value)}
              required
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">– waehlen –</option>
              {auftraege.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.titel} ({a.status})
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-slate-600">Von</label>
          <Input type="date" required value={von} onChange={(e) => setVon(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Bis (optional)</label>
          <Input type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
        </div>
      </div>
      <ErrorBanner error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Zuordnung speichern'}
        </Button>
      </div>
    </form>
  );
}

function ZuordnungRow({
  z,
  objekte,
  auftraege,
}: {
  z: PortalZuordnung;
  objekte: ObjektRef[];
  auftraege: AuftragRef[];
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  const beenden = useMutation({
    mutationFn: () =>
      api(`/api/v1/portal-zuordnungen/${z.id}/beenden`, { method: 'POST', body: {} }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['portal-zuordnungen', z.portalUserId] }),
    onError: (err) => setError(err),
  });
  const del = useMutation({
    mutationFn: () => api(`/api/v1/portal-zuordnungen/${z.id}`, { method: 'DELETE' }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['portal-zuordnungen', z.portalUserId] }),
    onError: (err) => setError(err),
  });

  let zielLabel = '–';
  if (z.objektId) {
    const o = objekte.find((x) => x.id === z.objektId);
    zielLabel = o ? `${o.bezeichnung}` : `Objekt ${z.objektId.slice(0, 8)}`;
  } else if (z.auftragId) {
    const a = auftraege.find((x) => x.id === z.auftragId);
    zielLabel = a ? a.titel : `Auftrag ${z.auftragId.slice(0, 8)}`;
  } else if (z.projektId) {
    zielLabel = `Projekt ${z.projektId.slice(0, 8)}`;
  }

  const aktiv = !z.bis || new Date(z.bis).getTime() > Date.now();

  return (
    <tr className={aktiv ? 'text-slate-800' : 'text-slate-400'}>
      <td className="px-2 py-1">{z.ressourceTyp}</td>
      <td className="px-2 py-1">{zielLabel}</td>
      <td className="px-2 py-1 text-slate-600">{z.rolle ?? '–'}</td>
      <td className="px-2 py-1 text-slate-600">{z.von}</td>
      <td className="px-2 py-1 text-slate-600">{z.bis ?? '—'}</td>
      <td className="px-2 py-1 text-right">
        {error instanceof Error && (
          <span className="text-xs text-red-600 mr-2">{error.message}</span>
        )}
        {aktiv && (
          <Button
            variant="ghost"
            onClick={() => {
              if (confirm('Zuordnung mit heutigem Datum beenden?')) beenden.mutate();
            }}
            disabled={beenden.isPending}
          >
            Beenden
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={() => {
            if (confirm('Zuordnung wirklich loeschen? (Audit-Trail bleibt erhalten.)'))
              del.mutate();
          }}
          disabled={del.isPending}
        >
          Loeschen
        </Button>
      </td>
    </tr>
  );
}
